import * as React from 'react';
import gql from 'graphql-tag';
import moment from 'moment';
import { useQuery } from 'react-apollo';
import RecentActorsOutlinedIcon from '@material-ui/icons/RecentActorsOutlined';
import { browserHistory } from '../../../components/AppContent';
import { linkToPage, showNotification } from '../../../App';
import { DueListRow, DueListWidget } from './DueListWidget';
import { formatDueLabel } from './formatDueLabel';
import EventSystem from '../../../EventSystem';
import { QUICK_VIEW_REFRESH_EVENT } from '../QuickViewDrawer';
import { runWithMinimumVisibleRefresh } from './RefreshingIndicator';
import { QuickViewEnquiryFilter } from './YourEnquiriesWidget';

const DUE_LIST_ROW_LIMIT = 5;

interface EnquiryFollowUpDueSummary {
    // the enquiry's guid (aliased in the query below), not the row number
    id: string;
    guid: string;
    firstName: string | null;
    lastName: string | null;
    natureOfEnquiry: string | null;
    areaOfLaw: string | null;
    status: string | null;
    isClosed: boolean;
    assignedToName: string | null;
    followUpDueDate: string | null;
    initialEnquiryDate: string | null;
    lastUpdated: string | null;
    isKeyOpportunity: boolean;
}

interface EnquiryFollowUpsDueData {
    enquiry: {
        summaryList: {
            recordCount: number;
            actualRowCountForSearch: number;
            summaries: EnquiryFollowUpDueSummary[];
        };
    };
}

interface EnquiryFollowUpsDueParams {
    offset: number;
    first: number;
    followUpDue: string;
    isMyEnquiriesOnly: boolean;
    isMyPersonalEnquiries: boolean;
    isIncludeCurrent: boolean;
    isKeyOpportunity: boolean;
}

// Compact slice of enquiry.summaryList, matching the mobile app's own ENQUIRY_LIST query
// field-for-field (RedView Mobile src/graphql/enquiries/enquiryQueries.ts) rather than
// EnquirySummaryPage.tsx's own ~130-field EnquirySummaryListData grid query - same filter
// params the mobile app's own DueTasksCardStack.tsx uses against this query
// (followUpDue: 'todayAndEarlier') - "todayAndEarlier" is FollowUpDueSelectorEnum's own string
// value (enquirySummary/selectors/FollowUpDueSelector.tsx), confirmed matching mobile's
// literal. `id`/`isKeyOpportunity` are the only two additions over mobile's own field list:
// `id` because this web app's Apollo cache already keys this exact type by `id` from
// EnquirySummaryListData elsewhere (a query that omits it here fails to write into the cache -
// the same class of bug KpiSummaryWidget.tsx hit and documents in its own comment),
// `isKeyOpportunity` because opening the record inline (EnquiryFormDrawer) needs it and mobile
// doesn't have that inline-open concept to need it.
//
// isMyEnquiriesOnly/isMyPersonalEnquiries - explicit request to scope this card to the
// signed-in person's own personal enquiries (isMyPersonalEnquiries: true) rather than the
// broader "assigned to me" toggle (isMyEnquiriesOnly), mirroring the mutual-exclusivity
// EnquirySummaryPage.tsx's own two toggles already enforce.
const EnquiryFollowUpsDueQuery = gql`
    query QuickViewEnquiryFollowUpsDue(
        $offset: Int
        $first: Int
        $followUpDue: String
        $isMyEnquiriesOnly: Boolean
        $isMyPersonalEnquiries: Boolean
        $isIncludeCurrent: Boolean
        $isKeyOpportunity: Boolean
    ) {
        enquiry {
            summaryList(
                offset: $offset
                first: $first
                followUpDue: $followUpDue
                isMyEnquiriesOnly: $isMyEnquiriesOnly
                isMyPersonalEnquiries: $isMyPersonalEnquiries
                isIncludeCurrent: $isIncludeCurrent
                isKeyOpportunity: $isKeyOpportunity
            ) {
                recordCount
                actualRowCountForSearch
                summaries {
                    # The server's own id is only the row's position in a result (1, 2, 3...), so Apollo - which keys cached
                    # objects by type + id - treated this list's rows and the Enquiries grid's rows as the same objects and let
                    # whichever query wrote last overwrite the other's. The guid is unique, so it is returned as the id.
                    id: guid
                    guid
                    firstName
                    lastName
                    natureOfEnquiry
                    areaOfLaw
                    status
                    isClosed
                    assignedToName
                    followUpDueDate
                    initialEnquiryDate
                    lastUpdated
                    isKeyOpportunity
                }
            }
        }
    }
`;

export interface EnquiryFollowUpsDueWidgetProps {
    /** Opens the enquiry inline (EnquiryFormDrawer, mounted once in QuickViewDrawer.tsx) rather than navigating away - explicit request. */
    onOpenEnquiry: (guid: string, isKeyOpportunity: boolean) => void;
}

export const EnquiryFollowUpsDueWidget: React.FC<EnquiryFollowUpsDueWidgetProps> = (props) => {
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    // Once the first page has actually landed, the full-card skeleton (isLoading below) must
    // never come back - explicit bug report: raw enquiryListQuery.loading briefly flips true
    // during fetchMore's own network round-trip too (not just the very first fetch), and
    // wiring that straight into DueListWidget's isLoading swapped its whole contents out for
    // the big panel skeleton on every page load, not just a small "loading more" row.
    const hasLoadedOnceRef = React.useRef(false);
    // Drives DueListWidget's own RefreshingIndicator - see YourEnquiriesWidget.tsx's identical
    // comment for why this is a separate flag from isLoading/hasLoadedOnceRef above.
    const [isRefreshing, setIsRefreshing] = React.useState(false);

    // batchGroup opts this widget's query out of the default shared batch bucket - see
    // YourEnquiriesWidget.tsx's identical comment.
    const enquiryListQuery = useQuery<EnquiryFollowUpsDueData, EnquiryFollowUpsDueParams>(EnquiryFollowUpsDueQuery, {
        variables: {
            offset: 0,
            first: DUE_LIST_ROW_LIMIT,
            followUpDue: 'todayAndEarlier',
            isMyEnquiriesOnly: false,
            isMyPersonalEnquiries: true,
            isIncludeCurrent: true,
            isKeyOpportunity: false,
        },
        context: { batchGroup: 'quickView-enquiryFollowUpsDue' },
    });

    React.useEffect(() => {
        if (enquiryListQuery.error) {
            showNotification('Failed to Fetch Enquiry Follow-ups Due', enquiryListQuery.error.message, 'error');
        }
    // tslint:disable-next-line: align
    }, [enquiryListQuery.error]);

    // Quick View's own header Refresh button (QuickViewDrawer.tsx) - see
    // YourEnquiriesWidget.tsx's own identical comment for why refetchRef/the empty-deps
    // effect/the explicit cleanup are each needed. refetch() re-fetches with this query's
    // original {offset:0, first:DUE_LIST_ROW_LIMIT} variables (fetchMore's own accumulated
    // pages live only in the merged cache result, not in these base variables), so refreshing
    // correctly resets the list back to its first page rather than re-requesting however many
    // rows infinite-scroll had loaded.
    const refetchRef = React.useRef(enquiryListQuery.refetch);
    refetchRef.current = enquiryListQuery.refetch;
    React.useEffect(() => {
        // Explicit request: also refresh on the Enquiry page's own create/update/delete
        // subscriptions (scoped 'enquiry' - see EnquirySummaryPage.tsx's onEnquiryCreated),
        // not just Quick View's header Refresh button (which emits with no scope, i.e.
        // "refresh everything" - only bail out when a scope is given AND it isn't ours).
        const onRefresh = (scope?: string) => {
            if (scope && scope !== 'enquiry') {
                return;
            }
            runWithMinimumVisibleRefresh(setIsRefreshing, () => refetchRef.current());
        };
        EventSystem.on(QUICK_VIEW_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, []);

    // Explicit request: "View all" now carries the same filter this card itself queries with
    // (followUpDue: today and earlier, scoped to the signed-in person's personal enquiries)
    // through to EnquirySummaryPage.tsx's own clearable Quick View filter chip, rather than
    // landing on an unfiltered grid - same drilldown pattern YourEnquiriesWidget.tsx's own
    // metric cards already use. Label is deliberately distinct from that widget's own
    // "Follow-up overdue" chip (a different source, different filter - isFollowUpOverdue
    // rather than followUpDue/isMyPersonalEnquiries) so the two aren't mistaken for each other.
    const onOpenEnquiriesSummary = () => {
        browserHistory.push({
            pathname: linkToPage('enquiriesSummary'),
            state: {
                quickViewEnquiryFilter: {
                    label: 'Your personal follow-ups due',
                    status: null,
                    assignedTo: null,
                    isMyEnquiriesOnly: false,
                    isMyPersonalEnquiries: true,
                    followUpDue: { value: 'todayAndEarlier', label: 'Today and earlier' },
                    isFollowUpOverdue: null,
                } as QuickViewEnquiryFilter,
            },
        });
    };

    const summaries = enquiryListQuery.data?.enquiry?.summaryList?.summaries;
    const totalCount = enquiryListQuery.data?.enquiry?.summaryList?.actualRowCountForSearch ?? 0;
    const hasMore = (summaries?.length ?? 0) < totalCount;

    if (summaries) {
        hasLoadedOnceRef.current = true;
    }

    // Infinite-scroll load-more (explicit request) - pages the same query forward from
    // however many summaries are already loaded, then appends the new page onto them.
    // updateQuery (rather than Apollo's own field-policy merge) keeps this call site
    // self-contained rather than needing a global typePolicy for this one compact query.
    const onLoadMore = () => {
        if (isLoadingMore || !hasMore) {
            return;
        }
        setIsLoadingMore(true);
        enquiryListQuery.fetchMore({
            variables: { offset: summaries?.length ?? 0, first: DUE_LIST_ROW_LIMIT },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) {
                    return previousResult;
                }
                return {
                    ...fetchMoreResult,
                    enquiry: {
                        ...fetchMoreResult.enquiry,
                        summaryList: {
                            ...fetchMoreResult.enquiry.summaryList,
                            summaries: [
                                ...previousResult.enquiry.summaryList.summaries,
                                ...fetchMoreResult.enquiry.summaryList.summaries,
                            ],
                        },
                    },
                };
            },
        // tslint:disable-next-line: no-any
        }).catch((reason: any) => {
            showNotification('Failed to Fetch Enquiry Follow-ups Due', reason?.message ?? reason, 'error');
        }).finally(() => {
            setIsLoadingMore(false);
        });
    };

    const rows: DueListRow[] | null = enquiryListQuery.error ? null : summaries?.map((enquiry) => {
        const due = formatDueLabel(enquiry.followUpDueDate);
        // Same name composition as the mobile app's own EnquiryListItem row - it doesn't
        // select a combined `name` field either, just firstName/lastName.
        const fullName = [enquiry.firstName, enquiry.lastName].filter(Boolean).join(' ');
        // Initials for the card variant's avatar chip - firstName/lastName first letters,
        // falling back to the first two letters of whatever name is available (guest/company-
        // style enquiries that only ever populate one of the two fields).
        const initials = (enquiry.firstName?.[0] ?? '') + (enquiry.lastName?.[0] ?? '')
            || fullName.slice(0, 2)
            || '?';
        return {
            key: enquiry.guid,
            primary: fullName || 'Enquiry',
            // areaOfLaw now shown as its own pill (categoryLabel below) rather than here, so
            // this is natureOfEnquiry only - explicit request, was a duplicate of the pill
            // whenever both were present, and just added height for no new information.
            secondary: enquiry.natureOfEnquiry ?? undefined,
            dueLabel: due.label,
            isOverdue: due.isOverdue,
            onClick: () => props.onOpenEnquiry(enquiry.guid, enquiry.isKeyOpportunity),
            initials: initials.toUpperCase(),
            statusLabel: enquiry.status,
            categoryLabel: enquiry.areaOfLaw,
            // "Received" dropped from the footer text (explicit bug report - the combined
            // string was too long to read, cutting off before the due label even showed) -
            // Updated + the due label are what actually matter for a follow-ups-due list.
            updatedLabel: enquiry.lastUpdated ? moment(enquiry.lastUpdated).format('D MMM YYYY') : null,
        };
    }) ?? null;

    return (
        <DueListWidget
            icon={<RecentActorsOutlinedIcon fontSize="small" />}
            iconBg="#e8f1fb"
            iconColor="#1667c7"
            title="Enquiry Follow-ups Due"
            isLoading={!hasLoadedOnceRef.current && enquiryListQuery.loading}
            isRefreshing={isRefreshing}
            rows={rows}
            totalCount={totalCount}
            emptyMessage="Nothing due on your enquiry follow-ups right now."
            onViewAll={onOpenEnquiriesSummary}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            variant="card"
        />
    );
};
